import { repereVue } from '../figures/vue3d.js';
import { hauteurDePyramide } from './solide.js';
/* ============================================================ les solides */
/** Les solides qu'on manipule : des polyèdres, dont chaque face est un polygone convexe. */
export const FORMES_MANIPULEES = ['cube', 'pave', 'prisme_triangulaire', 'pyramide'];
export const estManipulable = (s) => FORMES_MANIPULEES.includes(s.forme);
/** Les deux segments remarquables d'une pyramide qu'on peut désigner. */
export const REPERES_MARQUABLES = ['hauteur', 'apotheme'];
const plus3 = (a, b) => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });
const moins3 = (a, b) => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const fois3 = (a, k) => ({ x: a.x * k, y: a.y * k, z: a.z * k });
const scal3 = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
const croix3 = (a, b) => ({
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
});
const norme3 = (a) => Math.hypot(a.x, a.y, a.z);
const unit3 = (a) => {
    const n = norme3(a);
    return n < 1e-12 ? { x: 0, y: 0, z: 0 } : fois3(a, 1 / n);
};
const centre3 = (pts) => fois3(pts.reduce((s, p) => plus3(s, p), { x: 0, y: 0, z: 0 }), 1 / Math.max(1, pts.length));
/** L'identifiant d'une arête : ses deux lettres dans l'ordre alphabétique, jointes par « - ». */
export const idArete = (a, b) => (a < b ? `${a}-${b}` : `${b}-${a}`);
/** L'identifiant d'une face : ses lettres dans l'ordre alphabétique. */
export const idFace = (sommets) => [...sommets].sort().join('');
/**
 * Le POLYÈDRE NOMMÉ d'un solide manipulé : ses sommets (centré en x et z, posé
 * sur y = 0), ses faces orientées vers le dehors (vérifié contre le centre,
 * jamais supposé), ses arêtes.
 */
export function polyedreNomme(s) {
    const sommets = new Map();
    const brutes = [];
    const pieds = new Map();
    const reperes = new Map();
    const pave = (l, w, h) => {
        const bas = [
            ['A', -l / 2, w / 2],
            ['B', l / 2, w / 2],
            ['C', l / 2, -w / 2],
            ['D', -l / 2, -w / 2],
        ];
        const haut = ['E', 'F', 'G', 'H'];
        bas.forEach(([n, x, z], i) => {
            sommets.set(n, { x, y: 0, z });
            sommets.set(haut[i], { x, y: h, z });
        });
        brutes.push(['A', 'B', 'C', 'D'], ['E', 'F', 'G', 'H'], ['A', 'B', 'F', 'E'], ['B', 'C', 'G', 'F'], ['C', 'D', 'H', 'G'], ['D', 'A', 'E', 'H']);
    };
    switch (s.forme) {
        case 'cube':
            pave(s.arete, s.arete, s.arete);
            break;
        case 'pave':
            pave(s.longueur, s.largeur, s.hauteur);
            break;
        case 'prisme_triangulaire': {
            const [a, b, c] = s.cotes;
            /* A au départ du côté a (AB), C au bout du côté c (CA) : la convention de `hauteurDeLaBase`. */
            const px = (a * a + c * c - b * b) / (2 * a);
            const pz = Math.sqrt(Math.max(0, c * c - px * px));
            const tri = [
                ['A', 0, 0],
                ['B', a, 0],
                ['C', px, -pz],
            ];
            const cx = (0 + a + px) / 3;
            const cz = -pz / 3;
            const haut = ['D', 'E', 'F'];
            tri.forEach(([n, x, z], i) => {
                sommets.set(n, { x: x - cx, y: 0, z: z - cz });
                sommets.set(haut[i], { x: x - cx, y: s.hauteur, z: z - cz });
            });
            brutes.push(['A', 'B', 'C'], ['D', 'E', 'F'], ['A', 'B', 'E', 'D'], ['B', 'C', 'F', 'E'], ['C', 'A', 'D', 'F']);
            break;
        }
        case 'pyramide': {
            const c = s.cote;
            const h = hauteurDePyramide(s);
            sommets.set('A', { x: -c / 2, y: 0, z: c / 2 });
            sommets.set('B', { x: c / 2, y: 0, z: c / 2 });
            sommets.set('C', { x: c / 2, y: 0, z: -c / 2 });
            sommets.set('D', { x: -c / 2, y: 0, z: -c / 2 });
            sommets.set('S', { x: 0, y: h, z: 0 });
            brutes.push(['A', 'B', 'C', 'D'], ['A', 'B', 'S'], ['B', 'C', 'S'], ['C', 'D', 'S'], ['D', 'A', 'S']);
            pieds.set('O', { x: 0, y: 0, z: 0 });
            pieds.set('M', { x: 0, y: 0, z: c / 2 });
            reperes.set('hauteur', { de: 'S', a: 'O' });
            reperes.set('apotheme', { de: 'S', a: 'M' });
            break;
        }
    }
    const centre = centre3([...sommets.values()]);
    const faces = brutes.map((f) => {
        const pts = f.map((n) => sommets.get(n));
        const n = croix3(moins3(pts[1], pts[0]), moins3(pts[2], pts[0]));
        const dehors = scal3(n, moins3(centre3(pts), centre)) >= 0;
        const ordre = dehors ? f : [...f].reverse();
        return { sommets: ordre, id: idFace(ordre), nom: ordre.join('') };
    });
    const aretes = [];
    for (const f of faces) {
        f.sommets.forEach((a, i) => {
            const id = idArete(a, f.sommets[(i + 1) % f.sommets.length]);
            if (!aretes.includes(id))
                aretes.push(id);
        });
    }
    return { forme: s.forme, sommets, faces, aretes, pieds, reperes };
}
/** La normale sortante, unitaire, d'une face. */
export function normaleDeFace(p, f) {
    const pts = f.sommets.map((n) => p.sommets.get(n));
    return unit3(croix3(moins3(pts[1], pts[0]), moins3(pts[2], pts[0])));
}
/** Les arêtes d'une face, par identifiant, dans l'ordre de ses sommets. */
export function aretesDeFace(f) {
    return f.sommets.map((a, i) => idArete(a, f.sommets[(i + 1) % f.sommets.length]));
}
/** Les deux faces qui bordent une arête. */
export function facesDeLArete(p, arete) {
    return p.faces.filter((f) => aretesDeFace(f).includes(arete));
}
/* ============================================================== marquer */
/** Ce qu'on désigne : un sommet, une arête, une face, ou un segment (une arête ou un repère). */
export const QUOI_MARQUER = ['sommet', 'arete', 'face', 'segment'];
const NOM_REPERE = {
    hauteur: 'la hauteur',
    apotheme: 'l’apothème',
};
/** Le nom qu'on lit d'un élément, par son identifiant ; `null` : il n'existe pas sur ce solide. */
export function nomDElement(p, id) {
    if (p.sommets.has(id))
        return `le sommet ${id}`;
    if (p.aretes.includes(id))
        return `l’arête [${id.replace('-', '')}]`;
    const f = p.faces.find((x) => x.id === id);
    if (f !== undefined)
        return `la face ${f.nom}`;
    const r = p.reperes.get(id);
    if (r !== undefined)
        return `${NOM_REPERE[id]} [${r.de}${r.a}]`;
    return null;
}
/**
 * LES ÉLÉMENTS QU'ON PEUT DÉSIGNER, pour une tâche : tous ceux de la sorte
 * demandée — jamais seulement la réponse. Un `segment` couvre les arêtes ET
 * les repères demandés (la hauteur, l'apothème), dessinés en pointillé.
 */
export function elementsMarquables(p, quoi, reperes = []) {
    const sortie = [];
    if (quoi === 'sommet') {
        for (const n of p.sommets.keys())
            sortie.push({ id: n, sorte: 'sommet', nom: `le sommet ${n}` });
    }
    if (quoi === 'arete' || quoi === 'segment') {
        for (const a of p.aretes) {
            sortie.push({ id: a, sorte: 'arete', nom: nomDElement(p, a) });
        }
    }
    if (quoi === 'segment') {
        for (const r of reperes) {
            if (p.reperes.has(r))
                sortie.push({ id: r, sorte: 'repere', nom: nomDElement(p, r) });
        }
    }
    if (quoi === 'face') {
        for (const f of p.faces)
            sortie.push({ id: f.id, sorte: 'face', nom: `la face ${f.nom}` });
    }
    return sortie;
}
/** L'identifiant canonique d'une saisie : « EA » → « A-E », « ABFE » → « ABEF ». `null` : illisible. */
export function canoniserMarque(p, brut) {
    const t = brut.trim();
    if (p.sommets.has(t) || REPERES_MARQUABLES.includes(t))
        return t;
    const lettres = t.replace(/[[\]\s-]/gu, '');
    if (lettres.length === 2) {
        const id = idArete(lettres[0], lettres[1]);
        return p.aretes.includes(id) ? id : null;
    }
    const id = idFace(lettres.split(''));
    return p.faces.some((f) => f.id === id) ? id : null;
}
/** Ce qui part au serveur pour une marque : `marque=A-E`. */
export const ecrireMarque = (id) => `marque=${id}`;
/** La marque d'une réponse ; `null` : ce n'est pas une marque. */
export function lireMarque(reponse) {
    const m = /^\s*marque\s*=\s*(.+?)\s*$/u.exec(reponse);
    return m === null ? null : m[1];
}
/**
 * LE JUGEMENT D'UNE MARQUE : l'identité, exactement. `attendus` : un élément
 * ou plusieurs (« une arête parallèle à [AB] »). La hauteur prise pour
 * l'apothème (et l'inverse) se dit en mots.
 */
export function jugerMarque(p, attendus, reponse) {
    const brut = lireMarque(reponse);
    const choisi = brut === null ? null : canoniserMarque(p, brut);
    const voulus = attendus.map((a) => canoniserMarque(p, a) ?? a);
    if (choisi === null) {
        return {
            choisi: null,
            juste: false,
            constats: [{ etat: 'a_revoir', texte: 'Aucun élément n’est désigné sur le solide.' }],
        };
    }
    const nom = nomDElement(p, choisi);
    if (voulus.includes(choisi)) {
        return {
            choisi,
            juste: true,
            constats: [
                {
                    etat: 'tenu',
                    texte: `Tu as désigné ${nom} : c’est bien ${voulus.length > 1 ? 'un des éléments demandés' : 'l’élément demandé'}.`,
                },
            ],
        };
    }
    let texte = `Tu as désigné ${nom} : ce n’est pas ${voulus.length > 1 ? 'un des éléments demandés' : 'l’élément demandé'}.`;
    if (choisi === 'apotheme' && voulus.includes('hauteur')) {
        texte =
            'Tu as désigné l’apothème [SM], la hauteur d’une face. La hauteur de la pyramide part du sommet S et tombe d’aplomb sur la base, en son centre O.';
    }
    else if (choisi === 'hauteur' && voulus.includes('apotheme')) {
        texte =
            'Tu as désigné la hauteur [SO] de la pyramide. L’apothème est la hauteur d’une face : il va de S au milieu M d’un côté de la base.';
    }
    return { choisi, juste: false, constats: [{ etat: 'a_revoir', texte }] };
}
/** La face qui vient au bord `arete` de la face `face` : sa voisine dans le solide. */
export function faceVoisine(p, face, arete) {
    const f = p.faces.find((x) => x.id === face);
    if (f === undefined || !aretesDeFace(f).includes(arete))
        return null;
    return facesDeLArete(p, arete).find((x) => x.id !== face) ?? null;
}
/**
 * LE PATRON DONNÉ par l'énoncé : une face de départ, puis des plis. Chaque pli
 * attache la face voisine à la SEULE face du patron qui borde cette arête —
 * deux, et le pli serait ambigu ; aucune, il n'attache rien : c'est un grief.
 */
export function patronDonne(p, depart, plis) {
    const griefs = [];
    const racine = canoniserMarque(p, depart);
    if (racine === null || !p.faces.some((f) => f.id === racine)) {
        return { griefs: [`la face de départ « ${depart} » n’est pas une face du solide`] };
    }
    const patron = [{ face: racine, mere: null, pli: null, origine: 'donnee' }];
    for (const brut of plis) {
        const arete = canoniserMarque(p, brut);
        if (arete === null || !p.aretes.includes(arete)) {
            griefs.push(`le pli « ${brut} » n’est pas une arête du solide`);
            continue;
        }
        const meres = patron
            .map((f, i) => ({ f, i }))
            .filter(({ f }) => aretesDeFace(p.faces.find((x) => x.id === f.face)).includes(arete));
        if (meres.length !== 1) {
            griefs.push(meres.length === 0
                ? `le pli [${arete.replace('-', '')}] ne borde aucune face déjà posée`
                : `le pli [${arete.replace('-', '')}] borde deux faces déjà posées : il ne dit pas laquelle attacher`);
            continue;
        }
        const { f: mere, i } = meres[0];
        const voisine = faceVoisine(p, mere.face, arete);
        patron.push({ face: voisine.id, mere: i, pli: arete, origine: 'donnee' });
    }
    if (griefs.length > 0)
        return { griefs };
    if (new Set(patron.map((f) => f.face)).size !== patron.length) {
        return { griefs: ['le patron donné pose deux fois la même face'] };
    }
    return { patron };
}
/** Les arêtes d'une face du patron déjà prises par un pli (vers sa mère ou vers une fille). */
function plisDe(patron, rang) {
    const pris = new Set();
    const f = patron[rang];
    if (f !== undefined && f.pli !== null)
        pris.add(f.pli);
    patron.forEach((g) => {
        if (g.mere === rang && g.pli !== null)
            pris.add(g.pli);
    });
    return pris;
}
/** Les BORDS LIBRES du patron : où l'élève peut attacher une face. */
export function bordsLibres(p, patron) {
    const sortie = [];
    patron.forEach((f, i) => {
        const face = p.faces.find((x) => x.id === f.face);
        if (face === undefined)
            return;
        const pris = plisDe(patron, i);
        for (const a of aretesDeFace(face))
            if (!pris.has(a))
                sortie.push({ mere: i, arete: a });
    });
    return sortie;
}
/** Ce qui part au serveur : `plis=0/A-B,5/E-F`. */
export const ecrirePlis = (plis) => `plis=${plis.map((x) => `${String(x.mere)}/${x.arete}`).join(',')}`;
/** Les plis d'une réponse ; `null` : ce n'est pas une écriture de plis. */
export function lirePlis(reponse) {
    const m = /^\s*plis\s*=\s*(.*?)\s*$/u.exec(reponse);
    if (m === null)
        return null;
    if (m[1] === '')
        return [];
    const sortie = [];
    for (const morceau of m[1].split(',')) {
        const mm = /^\s*(\d+)\s*\/\s*([A-Z])-([A-Z])\s*$/u.exec(morceau);
        if (mm === null)
            return null;
        sortie.push({ mere: Number(mm[1]), arete: idArete(mm[2], mm[3]) });
    }
    return sortie;
}
/** Le patron de l'élève : le patron donné, puis ses faces, dans l'ordre où il les a attachées. */
export function patronAvecPlis(p, donne, plis) {
    const patron = [...donne];
    for (const pli of plis) {
        const mere = patron[pli.mere];
        if (mere === undefined)
            continue;
        if (!bordsLibres(p, patron).some((b) => b.mere === pli.mere && b.arete === pli.arete))
            continue;
        const voisine = faceVoisine(p, mere.face, pli.arete);
        if (voisine === null)
            continue;
        patron.push({ face: voisine.id, mere: pli.mere, pli: pli.arete, origine: 'eleve' });
    }
    return patron;
}
/**
 * LE PATRON À PLAT, vu du DEHORS du solide (y vers le haut). La première face
 * garde ses longueurs dans son plan ; chaque fille se déplie autour de son pli,
 * de l'autre côté que sa mère — ses longueurs et ses angles sont ceux du solide.
 */
export function disposerAPlat(p, patron) {
    const sortie = [];
    patron.forEach((f) => {
        const face = p.faces.find((x) => x.id === f.face);
        const pts = face.sommets.map((n) => p.sommets.get(n));
        if (f.mere === null || f.pli === null) {
            /* Le repère du plan de la face : x le long de la première arête, y = n × x. */
            const o = pts[0];
            const ex = unit3(moins3(pts[1], o));
            const ey = croix3(normaleDeFace(p, face), ex);
            sortie.push({
                sommets: face.sommets.map((nom, k) => {
                    const d = moins3(pts[k], o);
                    return { nom, x: scal3(d, ex), y: scal3(d, ey) };
                }),
            });
            return;
        }
        const mere = sortie[f.mere];
        const [u, v] = f.pli.split('-');
        const pu = mere.sommets.find((s) => s.nom === u);
        const pv = mere.sommets.find((s) => s.nom === v);
        const lu = Math.hypot(pv.x - pu.x, pv.y - pu.y);
        const e2 = { x: (pv.x - pu.x) / lu, y: (pv.y - pu.y) / lu };
        /* La normale au pli, du côté OPPOSÉ à la mère. */
        const cm = mere.sommets.reduce((s, q) => ({ x: s.x + q.x, y: s.y + q.y }), { x: 0, y: 0 });
        const cmx = cm.x / mere.sommets.length - pu.x;
        const cmy = cm.y / mere.sommets.length - pu.y;
        let n2 = { x: -e2.y, y: e2.x };
        if (n2.x * cmx + n2.y * cmy > 0)
            n2 = { x: -n2.x, y: -n2.y };
        const U = p.sommets.get(u);
        const e3 = unit3(moins3(p.sommets.get(v), U));
        sortie.push({
            sommets: face.sommets.map((nom, k) => {
                const d = moins3(pts[k], U);
                const s = scal3(d, e3);
                const h = norme3(moins3(d, fois3(e3, s)));
                return { nom, x: pu.x + s * e2.x + h * n2.x, y: pu.y + s * e2.y + h * n2.y };
            }),
        });
    });
    return sortie;
}
/** Deux polygones CONVEXES se chevauchent-ils (une aire commune, au-delà d'un frôlement) ? */
export function seChevauchent(a, b, eps = 1e-6) {
    for (const poly of [a, b]) {
        for (let i = 0; i < poly.length; i += 1) {
            const p = poly[i];
            const q = poly[(i + 1) % poly.length];
            const nx = -(q.y - p.y);
            const ny = q.x - p.x;
            const proj = (r) => {
                let lo = Infinity;
                let hi = -Infinity;
                for (const s of r) {
                    const v = s.x * nx + s.y * ny;
                    lo = Math.min(lo, v);
                    hi = Math.max(hi, v);
                }
                return [lo, hi];
            };
            const [a0, a1] = proj(a);
            const [b0, b1] = proj(b);
            const l = Math.hypot(nx, ny);
            if (a1 <= b0 + eps * l || b1 <= a0 + eps * l)
                return false;
        }
    }
    return true;
}
const IDENTITE = {
    r: [
        { x: 1, y: 0, z: 0 },
        { x: 0, y: 1, z: 0 },
        { x: 0, y: 0, z: 1 },
    ],
    t: { x: 0, y: 0, z: 0 },
};
const appliquer = (m, p) => plus3(plus3(plus3(fois3(m.r[0], p.x), fois3(m.r[1], p.y)), fois3(m.r[2], p.z)), m.t);
const composer = (m, n) => ({
    r: [appliquerVecteur(m, n.r[0]), appliquerVecteur(m, n.r[1]), appliquerVecteur(m, n.r[2])],
    t: appliquer(m, n.t),
});
function appliquerVecteur(m, v) {
    return plus3(plus3(fois3(m.r[0], v.x), fois3(m.r[1], v.y)), fois3(m.r[2], v.z));
}
/** La rotation d'angle `a` (radians) autour de l'axe passant par `o`, de direction unitaire `u` (Rodrigues). */
function rotationAutour(o, u, a) {
    const c = Math.cos(a);
    const s = Math.sin(a);
    const col = (e) => plus3(plus3(fois3(e, c), fois3(croix3(u, e), s)), fois3(u, scal3(u, e) * (1 - c)));
    const r = [
        col({ x: 1, y: 0, z: 0 }),
        col({ x: 0, y: 1, z: 0 }),
        col({ x: 0, y: 0, z: 1 }),
    ];
    const m = { r, t: { x: 0, y: 0, z: 0 } };
    return { r, t: moins3(o, appliquer(m, o)) };
}
/**
 * LE PATRON PLIÉ à l'instant `t` (0 : à plat ; 1 : refermé) — la position dans
 * l'espace de chaque face. À plat, le patron est dans le plan z = 0, le dehors
 * du solide vers +z ; chaque pli tourne sa fille de `t` fois l'angle entre les
 * normales des deux faces, vers −z : le solide se referme DERRIÈRE le papier.
 */
export function plierPatron(p, patron, aPlat, t) {
    const mouvements = [];
    const sortie = [];
    patron.forEach((f, i) => {
        const plat = aPlat[i];
        let m = IDENTITE;
        if (f.mere !== null && f.pli !== null) {
            const mere = aPlat[f.mere];
            const [u, v] = f.pli.split('-');
            const pu = mere.sommets.find((s) => s.nom === u);
            const pv = mere.sommets.find((s) => s.nom === v);
            const o = { x: pu.x, y: pu.y, z: 0 };
            const axe = unit3({ x: pv.x - pu.x, y: pv.y - pu.y, z: 0 });
            const rangMere = f.mere;
            const fm = p.faces.find((x) => x.id === patron[rangMere].face);
            const ff = p.faces.find((x) => x.id === f.face);
            const phi = Math.acos(Math.max(-1, Math.min(1, scal3(normaleDeFace(p, fm), normaleDeFace(p, ff)))));
            /* Le sens : celui qui envoie la fille vers −z (derrière le papier). */
            const c = plat.sommets.reduce((s, q) => ({ x: s.x + q.x, y: s.y + q.y }), { x: 0, y: 0 });
            const cf = { x: c.x / plat.sommets.length, y: c.y / plat.sommets.length, z: 0 };
            const essai = appliquer(rotationAutour(o, axe, 0.1), cf);
            const signe = essai.z < 0 ? 1 : -1;
            m = composer(mouvements[f.mere], rotationAutour(o, axe, signe * t * phi));
        }
        mouvements.push(m);
        sortie.push(plat.sommets.map((s) => ({ nom: s.nom, p: appliquer(m, { x: s.x, y: s.y, z: 0 }) })));
    });
    return sortie;
}
const nombreDeFaces = (n) => (n === 1 ? 'une face' : `${String(n)} faces`);
/**
 * LE JUGEMENT D'UN PATRON COMPLÉTÉ (CONCEPTION § 3, « Solide ») :
 *  - une face qui vient, au pliage, là où une autre est déjà : RECOUVREMENT ;
 *  - une face qui en chevauche une autre à plat : le patron n'est pas d'une pièce ;
 *  - des faces qui manquent : le patron ne se referme pas.
 * Les faces attachées sont, par construction, les voisines dans le solide.
 */
export function jugerDeveloppement(p, donne, plis) {
    const patron = patronAvecPlis(p, donne, plis);
    const aPlat = disposerAPlat(p, patron);
    const statuts = [];
    const vues = new Set();
    let bute = null;
    patron.forEach((f, i) => {
        if (f.origine === 'donnee') {
            statuts.push('donnee');
            vues.add(f.face);
            return;
        }
        if (vues.has(f.face)) {
            statuts.push('recouvre');
            if (bute === null)
                bute = i;
            return;
        }
        vues.add(f.face);
        const ici = aPlat[i].sommets;
        const chevauche = aPlat.slice(0, i).some((g) => seChevauchent(g.sommets, ici));
        statuts.push(chevauche ? 'chevauche' : 'juste');
    });
    const manquantes = p.faces.map((f) => f.id).filter((id) => !vues.has(id));
    const recouvrement = statuts.includes('recouvre');
    const chevauchement = statuts.includes('chevauche');
    const constats = [];
    const justes = statuts.filter((s) => s === 'juste').length;
    const ajoutees = patron.length - donne.length;
    if (justes > 0 && !recouvrement && !chevauchement) {
        constats.push({
            etat: 'tenu',
            texte: justes === 1
                ? 'Ta face se plie sans recouvrir une autre.'
                : 'Tes faces se plient sans se recouvrir.',
        });
    }
    else if (justes > 0) {
        constats.push({
            etat: 'tenu',
            texte: `${justes === 1 ? 'Une de tes faces vient' : `${String(justes)} de tes faces viennent`} à sa place au pliage.`,
        });
    }
    if (manquantes.length === 0 && !recouvrement && !chevauchement) {
        constats.push({
            etat: 'tenu',
            texte: 'Le patron se referme : chaque face du solide y est, une fois.',
        });
    }
    if (recouvrement) {
        constats.push({
            etat: 'a_revoir',
            texte: 'Au pliage, une de tes faces vient se poser sur une face déjà là : deux faces se recouvrent. Regarde le pliage jusqu’à la face qui bute.',
        });
    }
    if (chevauchement) {
        constats.push({
            etat: 'a_revoir',
            texte: 'À plat, une de tes faces en chevauche une autre : ce patron ne se découpe pas d’une seule pièce.',
        });
    }
    if (manquantes.length > 0) {
        constats.push({
            etat: 'a_revoir',
            texte: ajoutees === 0
                ? `Il manque ${nombreDeFaces(manquantes.length)} : le patron ne se referme pas.`
                : `Il manque encore ${nombreDeFaces(manquantes.length)} : le patron ne se referme pas.`,
        });
    }
    return {
        patron,
        statuts,
        bute,
        manquantes,
        constats,
        juste: manquantes.length === 0 && !recouvrement && !chevauchement,
        enPartie: justes > 0,
        recouvrement,
        chevauchement,
    };
}
/** Peut-on compléter ce patron ? (Pour l'import : un patron donné doit avoir au moins une complétion.) */
export function completionExiste(p, donne) {
    const essayer = (patron) => {
        const poses = new Set(patron.map((f) => f.face));
        if (poses.size === p.faces.length)
            return true;
        const aPlat = disposerAPlat(p, patron);
        for (const b of bordsLibres(p, patron)) {
            const v = faceVoisine(p, patron[b.mere].face, b.arete);
            if (v === null || poses.has(v.id))
                continue;
            const suite = [
                ...patron,
                { face: v.id, mere: b.mere, pli: b.arete, origine: 'eleve' },
            ];
            const ici = disposerAPlat(p, suite)[suite.length - 1].sommets;
            if (aPlat.some((g) => seChevauchent(g.sommets, ici)))
                continue;
            if (essayer(suite))
                return true;
        }
        return false;
    };
    return essayer(donne);
}
/** Projette un point de l'espace pour une vue (orthographique, celle de `vue3d.ts`). */
export function voir(p, vue) {
    const r = repereVue(vue);
    return { x: scal3(p, r.droite), y: scal3(p, r.haut), profondeur: scal3(p, r.regard) };
}
/** Une face est-elle tournée vers l'observateur, pour cette vue ? */
export function faceVue(p, f, vue) {
    return scal3(normaleDeFace(p, f), repereVue(vue).regard) > 1e-9;
}
/**
 * Une arête est cachée quand ses DEUX faces le sont (la règle de `vue3d.ts`) ;
 * un repère (la hauteur, l'apothème) est intérieur au solide ou sur une face.
 */
export function areteCachee(p, arete, vue) {
    return facesDeLArete(p, arete).every((f) => !faceVue(p, f, vue));
}
/** La vue de départ d'un solide manipulé : celle des manuels, de trois quarts, un peu d'en haut. */
export const VUE_SOLIDE_DEPART = { azimut: 30, elevation: 20 };
/** Le rayon de la sphère englobante (autour du centre des sommets) : le cadre ne respire pas en tournant. */
export function rayonEnglobant(p) {
    const pts = [...p.sommets.values()];
    const centre = centre3(pts);
    return { centre, rayon: Math.max(...pts.map((q) => norme3(moins3(q, centre)))) };
}
/**
 * CORRIGER UNE RÉPONSE SUR UN SOLIDE — la même fonction pour le serveur et le
 * banc : la marque par son identité, le patron par son pliage. `null` : le
 * solide n'est pas manipulable (l'import l'a refusé ; rien n'est inventé).
 */
export function corrigerSolide(solide, tache, vue, reponse) {
    if (!estManipulable(solide))
        return null;
    const p = polyedreNomme(solide);
    const v = vue ?? VUE_SOLIDE_DEPART;
    if (tache.type === 'marquer') {
        const attendus = typeof tache.attendu === 'string' ? [tache.attendu] : [...tache.attendu];
        const j = jugerMarque(p, attendus, reponse);
        return {
            juste: j.juste,
            enPartie: false,
            constats: j.constats,
            erreur: null,
            marque: j.choisi,
            dto: {
                solide,
                vue: v,
                constats: j.constats,
                tache: 'marquer',
                quoi: tache.quoi,
                ...(tache.reperes !== undefined ? { reperes: tache.reperes } : {}),
                choisi: j.choisi,
                attendus: attendus.map((a) => canoniserMarque(p, a) ?? a),
                juste: j.juste,
            },
        };
    }
    const r = patronDonne(p, tache.depart, tache.plis);
    if ('griefs' in r)
        return null;
    const plis = lirePlis(reponse) ?? [];
    const j = jugerDeveloppement(p, r.patron, plis);
    const erreur = j.recouvrement
        ? (tache.erreurs?.recouvrement ?? null)
        : j.chevauchement
            ? (tache.erreurs?.chevauchement ?? null)
            : null;
    return {
        juste: j.juste,
        enPartie: j.enPartie,
        constats: j.constats,
        erreur,
        marque: null,
        dto: {
            solide,
            vue: v,
            constats: j.constats,
            tache: 'developper',
            patron: j.patron,
            statuts: j.statuts,
            bute: j.bute,
            manquantes: j.manquantes.length,
        },
    };
}
/** La réponse sur un solide, en mots : ce que l'historique et la copie écrivent. */
export function reponseDeSolideEnMots(solide, reponse) {
    if (!estManipulable(solide))
        return reponse;
    const p = polyedreNomme(solide);
    const m = lireMarque(reponse);
    if (m !== null) {
        const id = canoniserMarque(p, m);
        return id === null ? 'Aucun élément désigné.' : `Désigné : ${nomDElement(p, id) ?? id}.`;
    }
    const plis = lirePlis(reponse);
    if (plis !== null) {
        return plis.length === 0
            ? 'Aucune face attachée.'
            : `${plis.length === 1 ? 'Une face attachée' : `${String(plis.length)} faces attachées`} au patron.`;
    }
    return reponse;
}
//# sourceMappingURL=solide-manipule.js.map